import { useMutation } from "@apollo/client";
import topicDiscussed from "@static/img/topic-discussed.svg";
import topicUndiscussed from "@static/img/topic-undiscussed.svg";
import moment from "moment";
import {
  ToggleTopicStateMutationMutation,
  ToggleTopicStateMutationMutationVariables,
  TopicState,
} from "types/graphql-schema";
import type { GetProfileOneononeQueryQuery } from "types/graphql-schema";

import toggleTopicStateMutation from "@apps/topic-cta/graphql/toggle-topic-state-mutation";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Tooltip from "@components/tooltip/tooltip";
import { assertEdgesNonNull, getAssetUrl, getUrl } from "@helpers/helpers";

type LastOneonone = NonNullable<
  NonNullable<
    NonNullable<GetProfileOneononeQueryQuery["lastOneonone"]>["edges"][0]
  >["node"]
>;

const ProfileLastOneononeCard = ({ meeting }: { meeting: LastOneonone }) => {
  const label = useLabel();
  const [toggleTopicState] = useMutation<
    ToggleTopicStateMutationMutation,
    ToggleTopicStateMutationMutationVariables
  >(toggleTopicStateMutation);

  const handleToggleDiscussed = (topicId: number, currentState: TopicState) => {
    const newState =
      currentState === TopicState.ClosedToDiscussion
        ? TopicState.Open
        : TopicState.ClosedToDiscussion;
    toggleTopicState({
      variables: { topicId, state: newState },
      refetchQueries: ["getProfileOneononeQuery"],
      onCompleted: () => {
        successNotificationVar({
          title:
            newState === TopicState.ClosedToDiscussion
              ? "Topic marked as discussed."
              : "Topic marked as undiscussed.",
          timeout: 3000,
        });
      },
    });
  };

  const topics = meeting.topics ? assertEdgesNonNull(meeting.topics) : [];
  const meetingDate = meeting.startDatetime
    ? moment(meeting.startDatetime).format("ddd, MMM D")
    : "";
  const meetingTitle = meeting.title || label("1-on-1");
  const summary = meeting.transcript?.summary;

  const discussedTopics = topics.filter(
    (t) => t.state === TopicState.ClosedToDiscussion,
  );
  const undiscussedTopics = topics.filter(
    (t) => t.state !== TopicState.ClosedToDiscussion,
  );
  const hasTopics = topics.length > 0;
  const hasSummaryOrTopics = summary || hasTopics;

  const meetingUrl = getUrl({
    meetingGroupId: meeting.meetingGroupId,
    meetingId: meeting.id,
  });

  return (
    <div className="flex flex-col gap-6 border rounded-md p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="tf-header-base-dark">
            Last {label("1-on-1", { titleCase: true })}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
            <span className="whitespace-nowrap">{meetingDate}</span>
            <span className="text-gray-300">•</span>
            <span className="truncate">{meetingTitle}</span>
          </div>
        </div>
        <Button
          theme={buttonTheme.lightBlue}
          size={ButtonSize.medium}
          to={meetingUrl}
        >
          Open last {label("1-on-1")}
        </Button>
      </div>

      {/* Summary or empty state */}
      {!hasSummaryOrTopics ? (
        <Callout icon="info">
          No summary available because no notes or transcription were used in
          this meeting.
        </Callout>
      ) : (
        <div className="flex flex-col gap-6">
          {summary && (
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
              {summary}
            </p>
          )}

          {hasTopics && (
            <div className="flex flex-col gap-2">
              <div className="tf-header-sm">Topics</div>

              <div className="flex flex-col gap-2">
                {/* Undiscussed topics first */}
                {undiscussedTopics.map((topic) => (
                  <div key={topic.id} className="flex items-center gap-2">
                    <Tooltip text="Mark topic as discussed">
                      <button
                        onClick={() =>
                          handleToggleDiscussed(topic.id, TopicState.Open)
                        }
                        className="flex items-center justify-center h-6 w-6 shrink-0 rounded hover:bg-gray-100"
                        aria-label={`Mark "${topic.title}" as discussed`}
                      >
                        <img
                          src={getAssetUrl(topicUndiscussed)}
                          className="w-4 h-4"
                        />
                      </button>
                    </Tooltip>
                    <div className="flex-1 text-sm text-gray-800 min-w-0">
                      {topic.title}
                    </div>
                  </div>
                ))}

                {/* Discussed topics */}
                {discussedTopics.map((topic) => (
                  <div key={topic.id} className="flex items-center gap-2">
                    <Tooltip text="Mark topic as undiscussed">
                      <button
                        onClick={() =>
                          handleToggleDiscussed(
                            topic.id,
                            TopicState.ClosedToDiscussion,
                          )
                        }
                        className="flex items-center justify-center h-6 w-6 shrink-0 rounded hover:bg-gray-100"
                        aria-label={`Mark "${topic.title}" as undiscussed`}
                      >
                        <img
                          src={getAssetUrl(topicDiscussed)}
                          className="w-4 h-4"
                        />
                      </button>
                    </Tooltip>
                    <div className="flex-1 text-sm text-gray-800 min-w-0">
                      {topic.title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileLastOneononeCard;
